/**
 * Synthetic contact windows for missions without a usable TLE (deep space,
 * L1, lunar). Ported from loader-orbits.js _generateDeepSpaceWindows
 * (lines 273 to 323): one track per contactSchedule entry per UTC day, at a
 * start hour derived from the antenna longitude.
 */
import type { Antenna, ContactWindow, Mission } from '@/features/loader/types';

const DAY_MS = 86_400_000;
const HOUR_MS = 3_600_000;

/**
 * Windows for one mission on one antenna between startMs and endMs.
 * `random` supplies the 0..1 draw behind maxElevation = 45 + random() * 30,
 * called once per emitted window in time order.
 */
export function generateDeepSpaceWindows(
  mission: Mission,
  antenna: Antenna,
  startMs: number,
  endMs: number,
  random: () => number,
): ContactWindow[] {
  // Only preferred or acceptable antennas may track the mission; empty lists allow every antenna.
  const preferred = mission.preferredAntennas || [];
  const alternates = mission.acceptableAlternatives || [];
  const allAllowed = preferred.concat(alternates);
  if (allAllowed.length > 0 && allAllowed.indexOf(antenna.id) === -1) {
    return [];
  }

  const compatible = antenna.capabilities.bands.some((band) => band === mission.requirements.bandType);
  if (!compatible) return [];

  const schedule = mission.orbit.contactSchedule;
  if (!schedule) return [];

  const windows: ContactWindow[] = [];
  const tracksPerDay = schedule.tracksPerDay || 1;
  const hoursPerTrack = schedule.hoursPerTrack || 4;

  // One track per day at a deterministic hour: the antenna's solar-time offset plus two hours.
  const lonOffset = ((antenna.location.lon + 180) / 360) * 24;
  const trackStartHour = (lonOffset + 2) % 24;

  const first = new Date(startMs);
  first.setUTCHours(Math.floor(trackStartHour), 0, 0, 0);
  let current = first.getTime();
  if (current < startMs) current += DAY_MS;

  while (current < endMs) {
    for (let t = 0; t < tracksPerDay; t++) {
      const trackStart = current + t * (24 / tracksPerDay) * HOUR_MS;
      const trackEnd = trackStart + hoursPerTrack * HOUR_MS;

      if (trackStart >= startMs && trackStart < endMs) {
        windows.push({
          startMs: trackStart,
          endMs: trackEnd,
          maxElevation: 45 + random() * 30,
          durationMin: hoursPerTrack * 60,
          synthetic: true,
        });
      }
    }
    current += DAY_MS;
  }

  return windows;
}
